// Frequently asked questions (home + pricing). Answers are plain data so the same text can feed
// search engines' FAQ markup.
import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import './faq.css';

const FAQS = [
  {
    q: 'Can I use Bumpups for free?',
    a: ['Yes. Bumpups offers a free plan with 60 minutes of video processing each month.', 'You can use it without entering any payment information.'],
  },
  {
    q: 'Which languages are supported?',
    a: ['Choose the language Bump AI answers in:'],
    list: ['English', 'Hindi', 'Spanish', 'Portuguese', 'Russian', 'German', 'French', 'Japanese', 'Korean', 'Arabic', 'Hebrew'],
  },
  {
    q: 'How long can my videos be?',
    a: ['Pro handles videos up to 3 hours 30 minutes long (YouTube links up to 3 hours). On the Free plan, videos can be up to your 60 monthly minutes.'],
  },
  {
    q: 'Can I upload local video files?',
    a: [
      'Yes. Upload MP4, MOV or WebM files up to 16 GB. Your first upload gets a free preview of its first 30 minutes, and Pro unlocks uploads in full.',
      'Bump AI transcribes what is said and also describes what is shown on screen, so slides, plays and demos count too.',
    ],
  },
  {
    q: 'Does Bumpups have an API?',
    a: ['Yes. Pro includes the Bumpups API: send a YouTube link from your own code and get timestamped chapters back, then ask questions about the video. It uses the same minutes as the web app. See bumpups.com/developers.'],
  },
];

const faqText = faq => [...faq.a, ...(faq.list ? [faq.list.join(', ') + '.'] : [])].join(' ');

const faqData = faqs => JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: faqText(f) } })),
});

// items: optional [question, answer] pairs for a page's own FAQ (default: the site-wide one)
export default function Faq({ title = 'Frequently asked questions', items }) {
  const FAQS_SHOWN = items ? items.map(([q, a]) => ({ q, a: [a] })) : FAQS;
  const FAQ_DATA = faqData(FAQS_SHOWN);
  const [open, setOpen] = useState(() => new Set());
  const toggle = i => setOpen(prev => {
    const next = new Set(prev);
    if (next.has(i)) next.delete(i); else next.add(i);
    return next;
  });

  return (
    <section className="mk-section">
      <Helmet><script type="application/ld+json">{FAQ_DATA}</script></Helmet>
      <div className="mk-container mk-container--narrow">
        <div className="mk-section-head">
          <h2 className="mk-h2">{title}</h2>
        </div>
        <div className="mkq">
          {FAQS_SHOWN.map((faq, i) => {
            const isOpen = open.has(i);
            return (
              <div key={faq.q} className={`mkq-item${isOpen ? ' is-open' : ''}`}>
                <h3 className="mkq-q">
                  <button type="button" aria-expanded={isOpen} aria-controls={`mkq-a-${i}`} onClick={() => toggle(i)}>
                    {faq.q}
                    <FontAwesomeIcon icon={faPlus} className="mkq-icon" />
                  </button>
                </h3>
                <div id={`mkq-a-${i}`} className="mkq-a" hidden={!isOpen}>
                  {faq.a.map(p => <p key={p}>{p}</p>)}
                  {faq.list && <ul>{faq.list.map(item => <li key={item}>{item}</li>)}</ul>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
